/**
 * Ability Context
 * 
 * Provides CASL abilities based on current user's role.
 * Integrates with AuthContext and permissionService.
 * Part of PE-1020: CASL Installation
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useSwitchUser } from './SwitchUserContext';
import { getCurrentUserRoles } from '../services/permissionService';
import { createMinimalAbility, createSystemAdminAbility, rolesToCaslAbility } from '../utils/caslConverter';
import { shouldBypassRoleChecks } from '../config/mvpConfig';
import { AppAbility } from '../types/casl';
import { Role } from '../services/providers/roleProvider';
import { getPrimaryRole } from '../services/rbac/rolePermissionSummary';

interface AbilityContextType {
  ability: AppAbility;
  roles: Role[];
  role: Role | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const AbilityContext = createContext<AbilityContextType | undefined>(undefined);

export const AbilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { isSwitched, currentUser } = useSwitchUser();
  const [ability, setAbility] = useState<AppAbility>(createMinimalAbility());
  const [roles, setRoles] = useState<Role[]>([]);
  const [role, setRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load abilities from current user's role
   */
  const loadAbilities = useCallback(async () => {
    if (!isAuthenticated) {
      setAbility(createMinimalAbility());
      setRole(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let resolvedRoles: Role[] = [];
      
      // If switched user, lookup their role from Gobodo
      if (isSwitched && currentUser) {
        // Use gobodoUserProvider directly to lookup switched user
        const { gobodoUserProvider } = await import('../services/providers/gobodoUserProvider');
        
        // Try to find user by email or ID
        let switchedUser = null;
        try {
          if (currentUser.email) {
            switchedUser = await gobodoUserProvider.getUserByEmail(currentUser.email);
          } else if (currentUser.id) {
            switchedUser = await gobodoUserProvider.getUserById(currentUser.id);
          }
        } catch (error) {
          console.warn('[AbilityContext] Failed to lookup switched user:', error);
        }
        
        if (switchedUser && switchedUser.id) {
          // Get roles via graph traversal
          resolvedRoles = await gobodoUserProvider.getUserRoles(switchedUser.id);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('[AbilityContext] Switched user role lookup:', currentUser.email, resolvedRoles.map((role) => role.name));
          }
        }
      } else {
        // Use current authenticated user's role
        resolvedRoles = await getCurrentUserRoles();
      }
      
      if (shouldBypassRoleChecks()) {
        setAbility(createSystemAdminAbility());
        setRoles(resolvedRoles);
        setRole(resolvedRoles.length > 0 ? getPrimaryRole(resolvedRoles) : null);
        if (process.env.NODE_ENV === 'development') {
          console.log(
            '[AbilityContext] RBAC bypass active — full CASL ability (Gobodo roles:',
            resolvedRoles.length ? resolvedRoles.map((r) => r.name).join(', ') : 'none',
            ')'
          );
        }
      } else if (resolvedRoles.length > 0) {
        const primaryRole = getPrimaryRole(resolvedRoles);
        const newAbility = rolesToCaslAbility(resolvedRoles);
        setAbility(newAbility);
        setRoles(resolvedRoles);
        setRole(primaryRole);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[AbilityContext] Loaded abilities for roles:', resolvedRoles.map((item) => item.name));
        }
      } else {
        // No role found, use minimal abilities
        setAbility(createMinimalAbility());
        setRoles([]);
        setRole(null);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[AbilityContext] No role found, using minimal abilities');
        }
      }
    } catch (err) {
      console.error('[AbilityContext] Failed to load abilities:', err);
      setError(err instanceof Error ? err : new Error('Failed to load abilities'));
      setAbility(createMinimalAbility());
      setRoles([]);
      setRole(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isSwitched, currentUser]);

  // Load abilities when auth state changes
  useEffect(() => {
    loadAbilities();
  }, [loadAbilities]);

  // Refresh abilities (exposed for manual refresh)
  const refresh = useCallback(async () => {
    await loadAbilities();
  }, [loadAbilities]);

  return (
    <AbilityContext.Provider
      value={{
        ability,
        roles,
        role,
        isLoading,
        error,
        refresh,
      }}
    >
      {children}
    </AbilityContext.Provider>
  );
};

/**
 * Hook to access ability context
 */
export const useAbilityContext = () => {
  const context = useContext(AbilityContext);
  if (context === undefined) {
    throw new Error('useAbilityContext must be used within an AbilityProvider');
  }
  return context;
};
